<template>
  <div class="not-found-board">
    <div class="not-found-board__card">
      <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6" class="not-found-board__icon" aria-hidden="true">
        <path d="M9 2.5 16 15H2z"/><path d="M9 7.5v3.5M9 13v.01"/>
      </svg>
      <h1>Page not found</h1>
      <p v-if="requestedPath" class="not-found-board__path">There's no page at <code>{{ requestedPath }}</code>.</p>
      <p v-else>That page doesn't exist.</p>
      <p class="not-found-board__hint">It may have moved, or the link might be out of date.</p>
      <div class="not-found-board__actions">
        <a href="#" class="not-found-board__link not-found-board__link--primary" @click.prevent="$router.push('/')">Overview</a>
        <a href="#" class="not-found-board__link" @click.prevent="$router.push('/servers')">Fleet</a>
      </div>
    </div>
  </div>
</template>

<script setup>
import commonProps from '@apexl/icarus-core/src/components/_properties/common';

// `not-found.html` — mounted at `/not-found` via
// `app\module\shell\Controller\NotFoundDisplayController::index()`. Reached
// only via the client-side redirect in `@root/services/notFoundGuard.js`
// when the SPA navigates to a path that doesn't match any registered
// dashboard page route — see that file for why this can't just be a plain
// vue-router route with its own component (icarus-core's router has no
// `<router-view>`; every path is server-driven through `Icarus.vue`'s
// page-fetch cycle, so this view is a page like any other).
defineProps({
  ...commonProps,
  requestedPath: {
    type: String,
    default: '',
  },
});
</script>

<style lang="scss">
.not-found-board {
  display: flex;
  justify-content: center;
  padding: 60px 20px;

  &__card {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    padding: 36px 40px;
    max-width: 440px;
    width: 100%;
    text-align: center;
  }

  &__icon {
    width: 34px;
    height: 34px;
    color: var(--theme-status-warning, #E0A030);
    margin-bottom: 12px;
  }

  h1 {
    font-size: 19px;
    font-weight: 600;
    margin: 0 0 10px;
  }

  p {
    font-size: 13px;
    color: var(--theme-text-secondary);
    margin: 0 0 6px;
  }

  &__path code {
    font-family: var(--theme-font-mono);
    font-size: 12px;
    background: var(--theme-bg-content);
    padding: 1px 6px;
    border-radius: 4px;
  }

  &__hint {
    margin-bottom: 0;
  }

  &__actions {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 20px;
  }

  &__link {
    font-size: 12.5px;
    font-weight: 600;
    padding: 7px 16px;
    border-radius: var(--theme-radius, 6px);
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);
    color: var(--theme-text-primary);
    text-decoration: none;
    cursor: pointer;

    &--primary {
      background: var(--theme-accent-primary);
      border-color: var(--theme-accent-primary);
      color: #fff;
    }
  }
}
</style>
